import { useEffect, useState } from "react";
import { CreditCard, Wallet, Copy, Check } from "lucide-react";
import api, { fmtINR, formatDetail } from "@/lib/api";

export default function PanelVirtualAccount() {
    const [wallet, setWallet] = useState(null);
    const [mids, setMids] = useState([]);
    const [err, setErr] = useState("");
    const [copied, setCopied] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const [w, m] = await Promise.all([
                    api.get("/partner/payouts/wallet"),
                    api.get("/partner/payouts/mids"),
                ]);
                setWallet(w.data); setMids(m.data.items || []);
            } catch (e) { setErr(formatDetail(e?.response?.data?.detail) || e.message); }
        })();
    }, []);

    const copy = async (text, key) => {
        try { await navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); } catch {}
    };

    return (
        <div data-testid="panel-virtual-account" className="p-6 space-y-5">
            <div>
                <h1 className="text-xl font-bold flex items-center gap-2"><CreditCard className="w-5 h-5" /> My Virtual Account</h1>
                <p className="text-xs text-slate-500 mt-1">Your prepaid wallet details & accepted funding sources.</p>
            </div>

            {err && <div className="bg-rose-50 border border-rose-200 rounded p-2 text-xs text-rose-700">{err}</div>}

            {/* Balance card */}
            <div className="bg-gradient-to-br from-indigo-600 to-blue-700 text-white rounded-2xl p-6">
                <div className="text-[11px] uppercase tracking-widest text-indigo-200 flex items-center gap-2"><Wallet className="w-4 h-4" /> Available balance</div>
                <div data-testid="va-balance" className="text-4xl font-bold mt-2 font-mono">{fmtINR(wallet?.balance ?? 0)}</div>
                <div className="text-xs text-indigo-200 mt-1">Currency: {wallet?.currency || "INR"}</div>
            </div>

            {/* Capabilities */}
            <div className="bg-white border border-slate-200 rounded-xl p-5">
                <h3 className="font-semibold text-sm">Capabilities</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    <div className="border border-slate-200 rounded-md p-3">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Payouts</div>
                        <div className="text-sm font-semibold mt-1">{mids.length > 0 ? `Enabled · ${mids.length} MID(s) active` : "Disabled"}</div>
                        <div className="text-[11px] text-slate-500 mt-1">IMPS · NEFT · RTGS supported</div>
                    </div>
                    <div className="border border-slate-200 rounded-md p-3">
                        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Partner ID</div>
                        <div className="flex items-center gap-2 mt-1">
                            <code className="text-sm font-mono">{wallet?.partner_id || "—"}</code>
                            {wallet?.partner_id && (
                                <button onClick={() => copy(wallet.partner_id, "pid")} className="text-slate-500">
                                    {copied === "pid" ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Funding note */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-xs text-amber-800">
                <strong>How to fund:</strong> This is a prepaid wallet. Contact our ops team to top-up — we'll credit your balance within 1 business hour of receiving the funds.
            </div>
        </div>
    );
}
